// shop-frontend/src/pages/ShopMallCategory.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { API_BASE, ASSET_BASE, getMyShopMallPage, getMyShopProducts } from "../utils/api";
import "../styles/shopMall.css";

/* =========================
   Helpers
   ========================= */
function s(v) {
  return String(v || "").trim();
}

function pickId(x) {
  return s(x?._id || x?.id || x?.productId || "");
}

function absUrl(u) {
  const x = s(u);
  if (!x) return "";
  if (x.startsWith("http://") || x.startsWith("https://")) return x;
  const base = String(ASSET_BASE || API_BASE || "https://moondala-one.onrender.com").replace(/\/+$/, "");
  if (x.startsWith("/")) return base + x;
  return base + "/" + x;
}

function pickTitle(p) {
  return s(p?.title || p?.name || "Untitled");
}

function pickImage(p) {
  return (
    absUrl(
      s(p?.image) ||
        s(p?.imageUrl) ||
        (Array.isArray(p?.images) ? p.images[0] : "") ||
        (Array.isArray(p?.media?.images) ? p.media.images[0] : "") ||
        ""
    )
  );
}

function pickPrice(p) {
  const n = Number(p?.localPrice ?? p?.price ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function pickCurrency(p) {
  return s(p?.currency || "USD").toUpperCase();
}

/* =========================
   Component
   ========================= */
export default function ShopMallCategory() {
  const nav = useNavigate();
  const loc = useLocation();
  const { categoryId } = useParams();

  const catId = useMemo(() => s(decodeURIComponent(categoryId || "")), [categoryId]);
  const backTo = s(loc?.state?.backTo) || "/shop/mall/edit";

  const aliveRef = useRef(true);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [section, setSection] = useState(null);
  const [products, setProducts] = useState([]);

  const [q, setQ] = useState("");

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  /* =========================
     Load section + products
     ========================= */
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr("");
        setSection(null);
        setProducts([]);

        const [mallRes, prodRes] = await Promise.all([
          getMyShopMallPage(),     // ✅ PRIVATE mall page
          getMyShopProducts(),     // ✅ ALL shop products
        ]);

        if (!alive || !aliveRef.current) return;

        const mallPage =
          mallRes?.mallPage && typeof mallRes.mallPage === "object"
            ? mallRes.mallPage
            : {};

        const sections = Array.isArray(mallPage.sections)
          ? mallPage.sections
          : [];

        // ✅ find EXACT category section (no guessing)
        const foundSection =
          sections.find(
            (sec) =>
              s(sec?.type) === "category" &&
              s(sec?.categoryId) === catId
          ) || null;

        setSection(foundSection);

        const list = Array.isArray(prodRes)
          ? prodRes
          : Array.isArray(prodRes?.products)
          ? prodRes.products
          : [];

        setProducts(list);
      } catch (e) {
        if (!alive || !aliveRef.current) return;
        setErr(e?.message || "Failed to load category");
      } finally {
        if (!alive || !aliveRef.current) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [catId]);

  /* =========================
     Derived
     ========================= */
  const sectionProductIds = useMemo(() => {
    if (!section || !Array.isArray(section.productIds)) return [];
    return section.productIds.map(String);
  }, [section]);

  const productMap = useMemo(() => {
    const m = new Map();
    (products || []).forEach((p) => {
      const id = pickId(p);
      if (id) m.set(id, p);
    });
    return m;
  }, [products]);

  const orderedProducts = useMemo(() => {
    return sectionProductIds
      .map((id) => productMap.get(id))
      .filter(Boolean);
  }, [sectionProductIds, productMap]);

  const filtered = useMemo(() => {
    const query = s(q).toLowerCase();
    if (!query) return orderedProducts;

    return orderedProducts.filter((p) => {
      return (
        pickTitle(p).toLowerCase().includes(query) ||
        s(p?.description).toLowerCase().includes(query)
      );
    });
  }, [orderedProducts, q]);

  /* =========================
     Render
     ========================= */
  if (loading) {
    return <div className="sm-card">Loading category…</div>;
  }

  if (err) {
    return <div className="sm-card sm-error">❌ {err}</div>;
  }

  if (!section) {
    return (
      <div className="sm-card">
        <b>Category not found in your Mall Builder.</b>
        <br />
        Go to <b>Mall Builder</b> and add this category section first.
      </div>
    );
  }

  return (
    <div className="sm-wrap">
      <div className="sm-container">
        {/* Header */}
        <div className="sm-head">
          <div>
            <div className="sm-title">{section.title || "Category"}</div>
            <div className="sm-sub">
              Showing <b>{filtered.length}</b> product(s)
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="sm-btn" onClick={() => nav(backTo)}>
              ← Back to Mall
            </button>
            <button className="sm-btn" onClick={() => nav("/shop/dashboard")}>
              Dashboard
            </button>
            <button className="sm-btn" onClick={() => nav("/shop/add-product")}>
              ➕ Add Product
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="sm-card" style={{ marginBottom: 12 }}>
          <input
            className="sm-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search in this section…"
          />
        </div>

        {/* Products */}
        {filtered.length === 0 ? (
          <div className="sm-card">No products in this section.</div>
        ) : (
          <div className="sm-grid">
            {filtered.map((p) => {
              const id = pickId(p);
              const img = pickImage(p);

              return (
                <div
                  key={id}
                  className="sm-prodCard"
                  onClick={() => nav(`/product/${encodeURIComponent(id)}`)}
                >
                  <div className="sm-prodTop">
                    <div className="sm-prodTitle">{pickTitle(p)}</div>
                    <span className="sm-pill">
                      {pickCurrency(p)} {pickPrice(p).toFixed(2)}
                    </span>
                  </div>

                  {img ? (
                    <img src={img} alt="" className="sm-img" />
                  ) : (
                    <div className="sm-noImg">No image</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
